import { useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWhatsAppService from '../../services/adminWhatsAppService';

/**
 * Pick one of the approved WhatsApp templates that suit this record (order, customer or lead) and send
 * it. The server fills the values from the record, so staff never type one. Templates still awaiting
 * Meta approval are listed but cannot be chosen.
 *
 * Templates that introduce a person ("This is Aanya from KAAY") ask for the sender's name; it is remembered
 * in this browser, because the admin login is shared and can't say who is actually writing.
 *
 * @param {{scope: 'orders' | 'customers' | 'leads', id: string, excludeKinds?: string[], className?: string}} props
 */
const SENDER_KEY = 'whatsappSenderName';
const readSender = () => {
  try {
    return localStorage.getItem(SENDER_KEY) || '';
  } catch {
    return '';
  }
};

const TemplateActions = ({ scope, id, excludeKinds = [], className = '' }) => {
  const toast = useToast();
  const [options, setOptions] = useState([]);
  const [kind, setKind] = useState('');
  const [sending, setSending] = useState(false);
  const [sender, setSender] = useState(readSender);

  useEffect(() => {
    let cancelled = false;
    adminWhatsAppService
      .listRecordTemplates(scope)
      .then((rows) => !cancelled && setOptions(rows))
      .catch(() => !cancelled && setOptions([]));
    return () => {
      cancelled = true;
    };
  }, [scope]);

  const visible = options.filter((o) => !excludeKinds.includes(o.kind));
  const selected = visible.find((o) => o.kind === kind);
  if (visible.length === 0) return null;

  const needsSender = !!selected?.needs_sender;
  const senderName = sender.trim();

  const send = async () => {
    setSending(true);
    try {
      if (needsSender) {
        try {
          localStorage.setItem(SENDER_KEY, senderName);
        } catch {
          /* remembering the name is a convenience only */
        }
      }
      await adminWhatsAppService.sendRecordTemplate(scope, id, selected.kind, needsSender ? senderName : undefined);
      toast.success(`${selected.label} queued on WhatsApp`);
      setKind('');
    } catch (err) {
      toast.error(err.message || 'Failed to send the WhatsApp message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={className}>
      <div className="flex gap-2">
        <select
          aria-label="WhatsApp template"
          value={selected ? selected.kind : ''}
          onChange={(e) => setKind(e.target.value)}
          className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-input rounded-lg bg-background"
        >
          <option value="">Send a WhatsApp template…</option>
          {visible.map((o) => (
            <option key={o.kind} value={o.kind} disabled={!o.approved}>
              {o.label}
              {o.approved ? '' : ' (awaiting approval)'}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={send}
          disabled={!selected || sending || (needsSender && !senderName)}
          className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
      {needsSender && (
        <input
          aria-label="Your name"
          value={sender}
          onChange={(e) => setSender(e.target.value)}
          maxLength={60}
          placeholder="Your name (who the customer will see it from)"
          className="mt-2 w-full px-3 py-1.5 text-sm border border-input rounded-lg bg-background"
        />
      )}
      {selected && <p className="mt-1 text-xs text-muted-foreground">{selected.preview}</p>}
    </div>
  );
};

export default TemplateActions;
